import { Box, Button, FormControlLabel, Grid, Switch } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { AsyncSelectValue } from "../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import Flex from "../../../../../components/Flex";
import FormHeadChip from "../../../../../components/FormHeadText";
import OutlineCard from "../../../../../components/OutlineCard";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import { ScrollArea } from "../../../../../components/ScrollBar";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";
import CustomTabs, { TabData } from "../../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { TIME_ZONE_LIST } from "../../../../../constants";
import { BOOKING_WINDOWS } from "../../../../../constants/master";
import {
  SlotData,
  initialDaysData,
} from "../../../../../constants/provider/availability";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAppointmentTypeGetAll } from "../../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
import { useEditAvailability } from "../../../../../queries/use-edit-availability";
import { useGetAvailabilitySettings } from "../../../../../queries/use-get-availability-settings";
import { useLocationsList } from "../../../../../queries/use-locationList";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import LinkButton from "../../patient/patient-list/patient-chart-new/patient-chronological-records-and-requiring-actions/LinkButton";
// import { AvailabilitySetting } from "./AvailabilitySetting";
import BlockDaysView from "./BlockDaysView";
import DeleteModal from "../../../../../components/app/DeleteModal";
import SlotCreationView from "./SlotCreationView";
import {
  DAYS_OF_WEEK,
  DAY_CHOICES,
  Days,
  WEEKDAY_CHOICES,
  timeToMinutes,
  validateSlots,
  validationSchema,
} from "./availability-contant";
import { getStoredRealm } from "@/utils/auth-storage";
interface EditAvailabilityModalProps {
  disclosureHost: DisclosureHost;
  providerIdObj?: AsyncSelectValue;
}

const EditAvailabilityModal = ({
  disclosureHost,
  providerIdObj,
}: EditAvailabilityModalProps) => {
  const [curDay, setCurDay] = useState<Days>(Days["MONDAY"]);
  const { mutateAsync: editAvailability } = useEditAvailability();

  const showSnackbar = useSnackbar();
  const [setToWeekdays, setSetToWeekdays] = useState(false);
  const DayChoices: TabData[] = setToWeekdays ? WEEKDAY_CHOICES : DAY_CHOICES;

  const validateEmptyTimeSlot = () => {
    const errors: any = { daySlots: [] };

    const lastInd = formik.values?.daySlots
      ?.map((el: any) => el.day)
      ?.lastIndexOf(curDay);

    const lastSlot = formik.values?.daySlots[lastInd];

    let isValid = true;

    if (lastSlot && !lastSlot.startTime) {
      errors.daySlots[lastInd] = {
        ...errors.daySlots[lastInd],
        startTime: "Start time is required",
      };
      isValid = false;
    }

    if (lastSlot && !lastSlot.endTime) {
      errors.daySlots[lastInd] = {
        ...errors.daySlots[lastInd],
        endTime: "End time is required",
      };
      isValid = false;
    }

    if (!isValid) {
      formik.setErrors(errors);
    }

    return isValid;
  };

  const queryClient = useQueryClient();

  const { data: appointmentTypeList } = useAppointmentTypeGetAll({
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  const formik = useFormik({
    initialValues: {
      ...initialDaysData,
      providerId: providerIdObj,
    },
    validationSchema: validationSchema,
    validate: (values: any) => validateSlots({ ...values, setToWeekdays }),
    enableReinitialize: true,
    onSubmit: async (v: any) => {
      const appointmentTypeSettings =
        appointmentTypeList?.data?.content?.map((it: any) => ({
          appointmentTypeUuid: it?.uuid,
          appointmentTypeName: it?.nameString,
          appointmentTypeNameFormatted: it?.nameString,
          slotTime: it?.duration,
          minNoticeUnit: null,
        })) ?? [];

      const filteredDaySlots = v.daySlots.filter((slot: any) => {
        const isValidTimeSlot = !!slot.startTime && !!slot.endTime;
        const isWeekday = setToWeekdays
          ? slot.day !== "SATURDAY" && slot.day !== "SUNDAY"
          : true;

        return isValidTimeSlot && isWeekday;
      });

      const minAppointmentDurationMinutes = Math.min(
        ...appointmentTypeSettings
          .filter(
            (s: any) =>
              s?.appointmentTypeUuid &&
              s?.slotTime !== undefined &&
              s?.slotTime !== ""
          )
          .map((s: any) => Number.parseInt(String(s.slotTime), 10))
      );

      const hasSlotWithInsufficientDuration = filteredDaySlots.some(
        (slot: any) => {
          const startMinutes = timeToMinutes(slot.startTime);
          const endMinutes = timeToMinutes(slot.endTime);
          const slotDurationMinutes =
            endMinutes >= startMinutes
              ? endMinutes - startMinutes
              : 24 * 60 - startMinutes + endMinutes;
          return slotDurationMinutes < minAppointmentDurationMinutes;
        }
      );

      if (hasSlotWithInsufficientDuration) {
        showSnackbar({
          type: "error",
          message:
            "The slot duration can not be less than the appointment type duration",
        });
        return;
      }

      return editAvailability({
        ...v,
        // bookBefore,
        providerId: v.providerId.value!,
        blockDays: v.blockDays?.filter((el: any) => !!el?.startTime),
        settings: appointmentTypeSettings as any,
        daySlots: filteredDaySlots as any,
        xTENANTID: getStoredRealm() ?? "",
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({ queryKey: ["slots-list"] });
          setTimeout(() => {
            formik.resetForm();
            disclosureHost.close();
          }, 1200);
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    },
  });

  useEffect(() => {}, [providerIdObj]);

  useEffect(() => {}, [formik.values]);

  const { data: providersList } = useProvidersList({
    status: true,
    pagination: {
      page: 0,
      pageSize: 500,
    },
    xTENANTID: getXtenantId(),
    enabled: disclosureHost.opened,
  });

  const { data: providerGroupLocationList } = useLocationsList({
    pagination: {
      page: 0,
      pageSize: 500,
    },
    status: true,
    providerGroupId: getXtenantId(),
  });

  const locationList = useMemo(() => {
    return providerGroupLocationList?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.name,
    }));
  }, [providerGroupLocationList?.data?.content]);

  const { data: providersAvailabilitySettings, refetch }: any =
    useGetAvailabilitySettings({
      providerUuid: formik.values.providerId.value!,
      providerGroupId: getStoredRealm() ?? "",
    });

  useEffect(() => {
    if (providersAvailabilitySettings?.data && disclosureHost.opened) {
      const ProviderData = providersList?.data?.content?.find(
        (p) => p.uuid === formik.values.providerId?.value
      );

      const ensureAllDaysPresent = (daySlots: SlotData["daySlots"]) => {
        return [
          ...daySlots,
          ...DAYS_OF_WEEK.filter(
            (day) => !daySlots.some((slot) => slot.day === day)
          ).map((day) => ({
            day,
            startTime: "",
            endTime: "",
            location: undefined,
            availabilityMode: "IN_PERSON",
          })),
        ];
      };
      const p = !providersAvailabilitySettings?.data?.daySlots.some(
        (slot: any) => slot.day === "SUNDAY" || slot.day === "SATURDAY"
      );

      setSetToWeekdays(p);
      if (p) {
        setCurDay(Days["MONDAY"]);
      }
      const emptySettingRow = {
        appointmentTypeUuid: "",
        appointmentTypeNameFormatted: "",
        appointmentTypeName: "",
        slotTime: undefined,
        minNoticeUnit: null,
        type: null,
      };

      const transformedSettings = providersAvailabilitySettings?.data?.settings
        ?.length
        ? providersAvailabilitySettings?.data?.settings?.map((setting: any) => {
            // If type is a string (old format like "NEW"), return empty row
            if (typeof setting.type === "string") {
              return emptySettingRow;
            }
            // If it's already in new format, return it with type object for AsyncSelect
            return {
              appointmentTypeUuid: setting.appointmentTypeUuid || "",
              appointmentTypeName: setting.appointmentTypeNameFormatted || "",
              appointmentTypeNameFormatted:
                setting.appointmentTypeNameFormatted || "",
              slotTime: setting.slotTime?.toString(),
              minNoticeUnit: setting.minNoticeUnit || null,
              // Ensure type is an object for AsyncSelect compatibility
              type:
                setting.type && typeof setting.type === "object"
                  ? setting.type
                  : setting.appointmentTypeUuid
                    ? {
                        value: setting.appointmentTypeUuid,
                        label: setting.appointmentTypeName,
                        duration: setting.slotTime,
                      }
                    : null,
            };
          })
        : [];

      // If settings is empty (new availability or no saved config), show one empty row
      const finalSettings =
        transformedSettings.length === 0
          ? [emptySettingRow]
          : transformedSettings;

      const reconvertedPayLoad = {
        ...providersAvailabilitySettings?.data,
        settings: finalSettings,
        bookingWindow: providersAvailabilitySettings?.data?.bookingWindow
          ? providersAvailabilitySettings?.data?.bookingWindow?.toString()
          : "",
        minNoticeTime:
          providersAvailabilitySettings?.data?.bookBefore?.split(" ")[0],
        minNoticeUnit:
          providersAvailabilitySettings?.data?.bookBefore?.split(" ")[1],
        blockDays: providersAvailabilitySettings?.data?.blockDays?.length
          ? providersAvailabilitySettings?.data?.blockDays
          : [
              {
                startTime: null,
                endTime: null,
                fullDay: true,
              },
            ],
        daySlots: ensureAllDaysPresent(
          providersAvailabilitySettings?.data?.daySlots
        ),
        providerId: ProviderData
          ? {
              value: ProviderData?.uuid as string,
              label: `${ProviderData?.firstName} ${ProviderData?.lastName}`,
            }
          : providerIdObj,
      };

      formik.setValues(reconvertedPayLoad);
    } else {
      const tz = providersList?.data?.content?.find(
        (p) => p.uuid === formik.values.providerId?.value
      )?.timezone;

      const temp = formik.values.providerId;
      formik.resetForm();
      setSetToWeekdays(false);
      if (tz) formik.setFieldValue("timezone", tz);
      formik.setFieldValue("providerId", temp);

      // Show one empty row for new availability
      formik.setFieldValue("settings", [
        {
          appointmentTypeUuid: "",
          appointmentTypeName: "",
          appointmentTypeNameFormatted: "",
          slotTime: undefined,
          minNoticeUnit: null,
          type: null,
        },
      ]);
    }
  }, [providersAvailabilitySettings?.data, disclosureHost.opened]);
  const deleteDisClosure = useDisclosure();
  const applytoAllDays = () => {
    const selectedDayArr = [...(formik?.values?.daySlots ?? [])].filter(
      (it: any) => it?.day === curDay
    );

    const newDayArray = [...DAY_CHOICES].reduce((acc, cur: any) => {
      if (
        setToWeekdays &&
        (cur.value === "SUNDAY" || cur.value === "SATURDAY")
      ) {
        const selectedDaySlots = formik.values?.daySlots?.find(
          (it: any) => it?.day === cur.value
        );

        acc.push(selectedDaySlots);
      } else {
        selectedDayArr.forEach((it: any) => {
          acc.push({ ...it, day: cur.value });
        });
      }

      return acc;
    }, [] as any);

    formik.setFieldValue("daySlots", newDayArray);
  };

  useEffect(() => {
    if (disclosureHost.opened) {
      refetch();
    }
  }, [disclosureHost.opened]);

  const validateEmptyTimeFields = (formik: any) => {
    const lastBlockDayIndex = formik.values.blockDays.length - 1;
    const lastBlockDay = formik.values.blockDays[lastBlockDayIndex];
    let isValid = true;

    // if (!lastBlockDay.startTime) {
    //   formik.setFieldError(
    //     `blockDays[${lastBlockDayIndex}].date`,
    //     "Date is required."
    //   );
    //   isValid = false;
    // }

    if (!lastBlockDay.startTime) {
      formik.setFieldError(
        `blockDays[${lastBlockDayIndex}].startTime`,
        "Date is required."
      );
      isValid = false;
    }

    if (!lastBlockDay.endTime) {
      formik.setFieldError(
        `blockDays[${lastBlockDayIndex}].endTime`,
        "End time is required."
      );
      isValid = false;
    }

    return isValid;
  };

  return (
    <StyledDialog
      open={disclosureHost.opened}
      maxWidth={"xl"}
      onClose={() => {
        formik.resetForm();
        disclosureHost.close();
      }}
      dialogTitle={<DialogTitle />}
      dialogAction={
        <Button
          disabled={formik.isSubmitting}
          variant="contained"
          onClick={formik.submitForm}
        >
          Save
        </Button>
      }
    >
      <Box width={"78vw"} height={"70svh"}>
        <Grid container spacing={1} height={"inherit"}>
          <Grid item container spacing={2} xs={12} marginBottom={"15px"}>
            <Grid item xs={3}>
              <ProviderSelectScroll
                required
                label="Provider"
                value={formik.values.providerId as AsyncSelectValue}
                // providerSearchParam={{ pagination: DEF_PAGINATION, searchKey: providersAvailabilitySettings?.data?.providerId }}
                prefillListOption={providerIdObj}
                onChange={(val) => {
                  formik.setFieldValue("providerId", val);
                }}
                handleBlur={formik.handleBlur}
                error={
                  (formik.touched?.providerId &&
                    formik.errors?.providerId) as any
                }
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelect
                required
                name="timezone"
                handleBlur={formik.handleBlur}
                label="Time Zone"
                value={formik.values.timezone}
                options={TIME_ZONE_LIST}
                onChange={(val) => formik.setFieldValue("timezone", val)}
                error={
                  (formik.touched?.timezone && formik.errors?.timezone) as any
                }
              />
            </Grid>
            <Grid item xs={2}>
              <CustomSelect
                required
                name="bookingWindow"
                handleBlur={formik.handleBlur}
                disableSortOptions
                label="Booking Window"
                options={BOOKING_WINDOWS}
                value={formik.values.bookingWindow}
                onChange={(option) => {
                  formik.setFieldValue(
                    "endTime",
                    option === "CUSTOM" ? new Date().toISOString() : undefined
                  );
                  formik.setFieldValue("bookingWindow", option);
                }}
                error={
                  (formik.touched?.bookingWindow &&
                    formik.errors?.bookingWindow) as any
                }
              />
            </Grid>

            {/* {formik.values.bookingWindow === "CUSTOM" && ( */}
            <Grid item xs={2}>
              <CustomDatePicker
                required
                name="startTime"
                handleBlur={formik.handleBlur}
                label="Start Date"
                placeholder="Select Date"
                minDate={
                  // !!providersAvailabilitySettings?.data?.daySlots?.length &&
                  // providersAvailabilitySettings?.data?.startTime
                  //   ? dayjs(providersAvailabilitySettings?.data?.startTime)
                  dayjs(new Date())
                }
                // maxDate={
                //   formik.values?.endTime
                //     ? dayjs(formik.values?.endTime)
                //     : undefined
                // }
                value={formik.values?.startTime}
                onChange={(val) => {
                  formik.setFieldValue(`startTime`, val);
                }}
                error={
                  (formik.touched?.startTime && formik.errors?.startTime) as any
                }
              />
            </Grid>
            {/* )} */}

            {formik.values.bookingWindow === "CUSTOM" && (
              <Grid item xs={2}>
                <CustomDatePicker
                  required
                  value={formik.values?.endTime}
                  minDate={dayjs(formik.values?.startTime)}
                  label="End Date"
                  placeholder="Select Date"
                  onChange={(val) => {
                    formik.setFieldValue("endTime", val);
                  }}
                  error={
                    (formik.touched?.endTime && formik.errors?.endTime) as any
                  }
                />
              </Grid>
            )}
          </Grid>

          <Grid item md={12} lg={12}>
            <ScrollArea height={"calc(100vh - 374px)"}>
              <FormHeadChip marginBottom={"10px"}>
                Day Slot Creation
              </FormHeadChip>
              <OutlineCard>
                <Flex flexDirection={"column"} gap={2} width={"100%"}>
                  <Flex gap={2} justifyContent={"space-between"}>
                    <CustomTabs
                      tabsData={DayChoices}
                      value={curDay}
                      onChange={(day) => {
                        setCurDay(day as Days);
                      }}
                    />
                    <Flex alignItems={"center"} gap={2}>
                      <LinkButton
                        underLineOnHover
                        onClick={() => deleteDisClosure.open()}
                      >
                        Apply to All Days
                      </LinkButton>
                      <DeleteModal
                        variant="warning"
                        w="380px"
                        open={deleteDisClosure.opened}
                        onClose={deleteDisClosure.close}
                        onConfirmation={() => {
                          applytoAllDays();
                          return Promise.resolve({
                            message: "Availability Applied to All Days",
                          });
                        }}
                        content={
                          "Do You want to overwrite the configured availability with the newly added?"
                        }
                      />
                      <Button
                        disableRipple
                        style={{
                          backgroundColor: "#1A1A1A0D",
                        }}
                      >
                        <FormControlLabel
                          checked={setToWeekdays}
                          value={setToWeekdays}
                          sx={{
                            marginLeft: "2px",
                          }}
                          control={
                            <Switch
                              value={setToWeekdays}
                              checked={setToWeekdays}
                              size="small"
                              onChange={(e) => {
                                setSetToWeekdays(e.currentTarget.checked);
                                if (e.currentTarget.checked) {
                                  setCurDay(Days["MONDAY"]);
                                }
                              }}
                            />
                          }
                          label={
                            <Text color={"black"} marginLeft={"3px"}>
                              Set to Weekdays
                            </Text>
                          }
                        />
                      </Button>
                    </Flex>
                  </Flex>

                  <Flex
                    gap={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Text>Time Slots</Text>
                    <StyledButtonV2
                      sx={{ fontSize: "14px" }}
                      label={"Add Time Slot"}
                      onClick={() => {
                        if (validateEmptyTimeSlot()) {
                          const values = formik.values.daySlots;

                          formik.setFieldValue(`daySlots`, [
                            ...values,
                            {
                              day: `${curDay}`,
                              startTime: "",
                              endTime: "",
                              location: undefined,
                              availabilityMode: "VIRTUAL",
                            },
                          ]);
                        }
                      }}
                      variant="text"
                      shouldAddIcon
                    />
                  </Flex>
                  <SlotCreationView
                    formik={formik}
                    curDay={curDay}
                    locationList={locationList as any}
                    onDeleteClick={(curDaySlots, ind) => {
                      const daySlots = formik.values?.daySlots;

                      if (curDaySlots.length === 1) {
                        const slotsAfterClearCurDayEl = daySlots?.toSpliced(
                          ind,
                          1,
                          {
                            day: `${curDay}`,
                            startTime: "",
                            endTime: "",
                            location: undefined,
                            availabilityMode: "VIRTUAL",
                          }
                        );

                        formik.setFieldValue(
                          `daySlots`,
                          slotsAfterClearCurDayEl
                        );
                        return;
                      }

                      const dataAfterDeleteSlot = daySlots?.toSpliced(ind, 1);

                      formik.setFieldValue(`daySlots`, dataAfterDeleteSlot);
                    }}
                  />
                </Flex>
              </OutlineCard>

              {/* block days card */}
              <FormHeadChip marginBottom={"10px"} marginTop={"30px"}>
                Block Days
              </FormHeadChip>
              <OutlineCard sx={{ marginTop: 2 }}>
                <Flex flexDirection={"column"} gap={2}>
                  <Flex
                    gap={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Text>Days</Text>
                    <StyledButtonV2
                      // fontSize="14px"
                      label={"Add Block Days"}
                      onClick={() => {
                        if (validateEmptyTimeFields(formik)) {
                          const values = formik.values?.blockDays;
                          formik.setFieldValue(`blockDays`, [
                            ...values,
                            initialDaysData?.blockDays[0],
                          ]);
                        }
                      }}
                      variant="text"
                      shouldAddIcon
                    />
                  </Flex>

                  <Grid container xs={12}>
                    <BlockDaysView
                      formik={formik}
                      onDeleteClick={(ind) => {
                        // this may be change if they need to delete first element as well un comment this
                        const blockSlots = formik.values?.blockDays;
                        if (ind === 0 && blockSlots.length === 1) {
                          formik.setFieldValue(`blockDays`, [
                            {
                              startTime: null,
                              endTime: null,
                              fullDay: true,
                            },
                          ]);

                          return;
                        }

                        const dataAfterDeleteSlot = blockSlots?.toSpliced(
                          ind,
                          1
                        );

                        formik.setFieldValue(`blockDays`, dataAfterDeleteSlot);
                      }}
                    />
                  </Grid>
                </Flex>
              </OutlineCard>
            </ScrollArea>
          </Grid>

          {/* <Grid item md={12} lg={4.5} flexDirection={"column"}>
            <FormHeadChip mb={"10px"}>Availability Settings</FormHeadChip>

            <OutlineCard style={{ height: "375px" }}>
              <AvailabilitySetting formik={formik} />
            </OutlineCard>
          </Grid> */}
        </Grid>
      </Box>
    </StyledDialog>
  );
};

export default EditAvailabilityModal;

const DialogTitle = () => (
  <Flex flexDirection={"column"}>
    <Text fontSize={"16px"} fontWeight={"medium"}>
      Edit Availability
    </Text>
    <Text c="primary.main" marginTop={"15px"}>
      Please Note: Changing availability settings may take several minutes to
      update.
    </Text>
  </Flex>
);
